import { Button, Col, Form, Input, Row } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { _adr } from "../../../address";
import { useDealer } from "../../../tools/api";
import { errorMessage } from "../../../tools/toastifyShowMessage";
import { convertPersian, convertToJalaliWithoutTime, formatMsisdn, isEmpty, validateInput } from "../../../tools/tools";
import { clearGlobalFormData, globalValue } from "../../../reducer/serviceReducerGlobala";
import { simcardPurchase } from "../../../reducer/serviceReducer";
import IncressCharge from "../../auxiliary/incressCharge/charge";
import Loading from "../../auxiliary/loader/loading";
import ShopPage, { toman } from "../../auxiliary/shop/ShopPage";
import { promoBeforePrice } from "../shop/sp-kit/bundle";
import { ESIM_STEPS } from "./esimBundle";
import "./esim.scss";

const PLANID = Number(process.env.REACT_APP_PLANID_ESIM);
const WALLET_SHORTAGE = 1126;

// eSIM pre-invoice, same frame and cards as the charge / package / bill pre-invoices (WalletPrefactor);
// its own logic because every amount (bundle, eSIM, tax, total, commission, shortage) comes from CalculateSPCommissionPrice
const EsimPrefactor = () => {
	const [t] = useTranslation("global");
	const nav = useNavigate();
	const dispatch = useDispatch();
	const [searchParam] = useSearchParams();
	const another = useSelector((state) => state?.another);
	const { calculateSPCommissionPrice, isValidDiscountCode, queryMethodOfReceiptByPlanId, redirectToNeginWalletGateWayEsim } = useDealer();
	const bankRef = useRef(null);

	// back from the bank (?localInvoiceId=…): the sale was saved in the persisted store before leaving
	const [resume] = useState(() => (isEmpty(searchParam.get("localInvoiceId")) ? "" : searchParam.get("localInvoiceId")));
	const fromState = useSelector((state) => state?.from?.spPurchase);
	const [sale] = useState(() => (resume ? another?.spPurchase : fromState) || {});
	const { profile = {}, spbundle: bundle = {}, spMsisdn = [] } = sale;
	const msisdn = spMsisdn[0]?.msisdn;

	const [invoice, setInvoice] = useState(null);
	const [discountCode, setDiscountCode] = useState("");
	const [methodReceiptId, setMethodReceiptId] = useState(null);
	const [shortage, setShortage] = useState(0);
	const [openCharge, setOpenCharge] = useState(!!resume);
	const [localInvoiceId, setLocalInvoiceId] = useState(resume);
	const [isLoading, setIsLoading] = useState(false);
	const wallet = another?._currentUser?.walletAmount;

	useEffect(() => {
		// back from the bank: put the sale back where the next pages read it and drop the backup
		if (resume) {
			dispatch(simcardPurchase({ spPurchase: sale }));
			dispatch(clearGlobalFormData(["spPurchase"]));
		}
		// methodreceiptid must come from the eSIM plan's list, not the physical one
		queryMethodOfReceiptByPlanId({ planid: PLANID, cityid: profile?.cityid }).then((res) => {
			if (res?.length) setMethodReceiptId((res.find((item) => item.methodIsDefault) || res[0]).methodOfReceiptId);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// recalculated after a discount code or a wallet top-up
	useEffect(() => {
		if (!bundle?.bundleSalesSimId || !msisdn) return;
		setIsLoading(true);
		calculateSPCommissionPrice({ spbundleid: bundle.bundleSalesSimId, msisdn, iccid: "", certno: profile?.certno, discountcode: discountCode })
			.then((res) => {
				if (res) {
					setInvoice(res);
					setShortage(res.shortageAmount > 0 ? res.shortageAmount : 0);
				}
			})
			.finally(() => setIsLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [discountCode, wallet]);

	const handleDiscount = async ({ discount }) => {
		if (discountCode) return setDiscountCode("");
		setIsLoading(true);
		const res = await isValidDiscountCode({ discountcode: discount, bundlesalessimid: bundle.bundleSalesSimId, certno: profile?.certno, planid: PLANID });
		setIsLoading(false);
		if (res && !res.HasError) setDiscountCode(discount);
	};

	// the gateway answers with an auto-posting bank form; an empty answer means the modal was closed
	const handleCallBackCharge = (value) => {
		if (value?.length > 0) {
			dispatch(globalValue({ spPurchase: sale }));
			bankRef.current.innerHTML = value;
			document.querySelector('form[action="https://asan.shaparak.ir"]').submit();
		} else {
			setLocalInvoiceId("");
			setOpenCharge(false);
		}
	};

	const handleSubmit = async () => {
		if (shortage > 0) return setOpenCharge(true);
		setIsLoading(true);
		try {
			const res = await redirectToNeginWalletGateWayEsim({
				planid: PLANID,
				methodreceiptid: methodReceiptId,
				bundlesalesimid: bundle.bundleSalesSimId,
				msisdn: [msisdn],
				iccid: "",
				certno: profile?.certno,
				discountcode: discountCode,
			});
			if (!res) return errorMessage(process.env.REACT_APP_ERR_SERVICES);
			if (!res.HasError) {
				nav(_adr.Root + _adr.SimCardPurchase.Index + _adr.SimCardPurchase.Esim.Document, { state: { ...res.Result, message: res.Message } });
			} else if (res.Code === WALLET_SHORTAGE && res.Result?.shortageAmount > 0) {
				setShortage(res.Result.shortageAmount);
				setOpenCharge(true);
			} else {
				errorMessage(res.Message);
			}
		} finally {
			setIsLoading(false);
		}
	};

	// [label, amount, struck-through old price]: eSIM's from the invoice, the bundle's from the bundle list (promotion window)
	const rows = invoice
		? [
				["قیمت باندل", invoice.bundlePrice, promoBeforePrice(bundle)],
				invoice.discountAmount > 0 && ["تخفیف", invoice.discountAmount],
				["قیمت eSIM", invoice.esimPrice, invoice.esimBeforePrice],
				["مالیات بر ارزش افزوده", invoice.taxAmount],
				["مبلغ قابل دریافت از مشتری", invoice.totalAmount],
				[invoice.commission > 0 ? "مبلغ واریز به کیف پول" : "مبلغ برداشت از کیف پول", Math.abs(invoice.commission)],
				["سود شما از این فروش", (invoice.packageSalesCommission || 0) + (invoice.bundleSalesCommission || 0)],
		  ].filter(Boolean)
		: [];

	const profileItems = [
		["نام", profile.fname],
		["نام خانوادگی", profile.lname],
		["کد ملی", profile.certno],
		["تاریخ تولد", convertToJalaliWithoutTime(profile.birthdate)],
		["نام پدر", profile.fathername],
		["شماره شناسنامه", profile.identityno],
		["جنسیت", profile.txtGender],
		["موبایل", <span dir="ltr">0{formatMsisdn(profile.smsmobileno?.slice(1))}</span>],
		["استان", profile.txtProvince],
		["شهر", profile.txtCity],
		["کدپستی", profile.postalcode],
	];

	return (
		<>
			<ShopPage
				title="فروش eSIM"
				prefix="sp"
				steps={ESIM_STEPS.map((step) => step.title)}
				step={3}
				footer={
					<>
						<Button type="primary" className="btn-normal btn-aptel" danger onClick={handleSubmit} disabled={!invoice || !methodReceiptId}>
							تأیید و پرداخت
						</Button>
						<Button type="default" className="btn-normal" onClick={() => nav(_adr.Root + _adr.SimCardPurchase.Index + _adr.SimCardPurchase.Profile)}>
							بازگشت
						</Button>
					</>
				}>
				<div className="shop-invoice">
					<Row gutter={[16, 16]}>
						<Col span={14} xl={14} lg={14} md={24} sm={24} xs={24}>
							<div className="inv-card">
								<h3>صورت حساب مشتری</h3>
								<div className="inv-body">
									<div className="inv-sim">
										<span className="inv-sim-icon">
											<i className="icon-simcard"></i>
										</span>
										<div>
											<small>eSIM - {bundle?.bundleName} با شماره</small>
											<strong dir="ltr">{formatMsisdn(msisdn)}</strong>
										</div>
									</div>
									<h4>
										اطلاعات خریدار{" "}
										<Link to={_adr.Root + _adr.SimCardPurchase.Index + _adr.SimCardPurchase.Profile}>
											<i className="icon-edit"></i>
										</Link>
									</h4>
									<div className="list-item">
										{profileItems.map(([label, value]) => (
											<div className="item" key={label}>
												<div className="label">{label}:</div>
												<div className="value">{value}</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</Col>
						<Col span={10} xl={10} lg={10} md={24} sm={24} xs={24}>
							<div className="inv-card">
								<h3>صورت حساب فروشنده</h3>
								<div className="inv-body">
									<Form className="esim-discount" layout="vertical" onFinish={handleDiscount}>
										<Form.Item label="کد تخفیف" name="discount" rules={[validateInput({ t: t, validate: true })]}>
											<Input
												autoComplete="off"
												onChange={(e) => (e.target.value = convertPersian(e.target.value))}
												suffix={
													<Button htmlType="submit" className="btn-normal btn-aptel">
														{discountCode ? "حذف" : "اعمال"}
													</Button>
												}
												maxLength="32"
											/>
										</Form.Item>
									</Form>
									<ul className="inv-rows">
										{rows.map(([label, amount, old]) => (
											<li key={label}>
												<span>{label}</span>
												<span className="value">
													{!!Number(old) && Number(old) !== Number(amount) && <del className="esim-old-price">{toman(old)}</del>}
													{toman(amount)}
												</span>
											</li>
										))}
									</ul>
									{invoice &&
										(shortage > 0 ? (
											<div className="inv-pay error">
												<h3>اعتبار شما کافی نمی‌باشد</h3>
												<div>
													کسری اعتبار: <b>{toman(shortage)}</b>
												</div>
												<Button className="btn-normal btn-aptel" htmlType="button" onClick={() => setOpenCharge(true)}>
													افزایش اعتبار
												</Button>
											</div>
										) : (
											<div className="inv-pay success">
												<p>{invoice.commission > 0 ? "این مبلغ به کیف پول شما واریز می‌شود" : "این مبلغ از کیف پول شما برداشت می‌شود"}</p>
												<h3>{toman(Math.abs(invoice.commission))}</h3>
												<div className="inv-after">
													<span>موجودی پس از خرید</span>
													<b>{toman(wallet + invoice.commission)}</b>
												</div>
											</div>
										))}
								</div>
							</div>
						</Col>
					</Row>
				</div>
			</ShopPage>
			<IncressCharge
				open={openCharge}
				planid={process.env.REACT_APP_PLANID_CHARGE_WALLET_ESIM}
				msisdn={another?._currentUser?.telnoone}
				price={shortage}
				callback={handleCallBackCharge}
				localInvoiceId={localInvoiceId}
			/>
			<Loading isOpen={isLoading} />
			<div ref={bankRef}></div>
		</>
	);
};

export default EsimPrefactor;
